<template>
  <div
    class="tutorial-overlay"
    :class="{ 'allow-interaction': allowInteraction }"
    @click.self="handleBackdropClick"
  >
    <!-- Backdrop with spotlight cutout -->
    <div class="tutorial-backdrop">
      <!-- Spotlight hole (transparent area) -->
      <div
        v-if="targetRect"
        class="tutorial-spotlight"
        :class="{ 'interactive': allowInteraction }"
        :style="spotlightStyle"
      />
    </div>

    <!-- Tooltip -->
    <div class="tutorial-tooltip-wrapper">
      <TutorialTooltip
        v-if="currentStep"
        :title="currentStep.title"
        :content="currentStep.content"
        :position="currentStep.position || 'bottom'"
        :target-rect="targetRect"
        :show-prev="!isFirstStep"
        :show-next="!currentStep.hideNextButton"
        :is-last-step="isLastStep"
        :tooltip-offset="currentStep.tooltipOffset"
        @prev="$emit('prev')"
        @next="$emit('next')"
      />
    </div>

    <!-- Progress indicator at bottom -->
    <div class="tutorial-footer">
      <TutorialProgress
        :current="currentStepIndex"
        :total="totalSteps"
        :show-skip="false"
        @dot-click="handleDotClick"
      />
    </div>
  </div>
</template>

<script>
import TutorialTooltip from './TutorialTooltip.vue';
import TutorialProgress from './TutorialProgress.vue';

export default {
  name: 'TutorialOverlay',

  components: {
    TutorialTooltip,
    TutorialProgress
  },

  props: {
    currentStep: {
      type: Object,
      default: null
    },
    currentStepIndex: {
      type: Number,
      default: 0
    },
    totalSteps: {
      type: Number,
      default: 0
    },
    targetRect: {
      type: Object,
      default: null
    },
    isFirstStep: {
      type: Boolean,
      default: true
    },
    isLastStep: {
      type: Boolean,
      default: false
    }
  },

  emits: ['prev', 'next', 'go-to-step'],

  computed: {
    spotlightStyle() {
      if (!this.targetRect) return {};

      const style = {
        top: `${this.targetRect.top}px`,
        left: `${this.targetRect.left}px`,
        width: `${this.targetRect.width}px`,
        height: `${this.targetRect.height}px`
      };

      // Apply custom border radius if specified
      if (this.currentStep && this.currentStep.spotlightBorderRadius) {
        style.borderRadius = this.currentStep.spotlightBorderRadius;
      }

      return style;
    },

    allowInteraction() {
      return this.currentStep && this.currentStep.allowInteraction === true;
    }
  },

  mounted() {
    // Add keyboard navigation
    document.addEventListener('keydown', this.handleKeydown);
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
    document.body.style.overflow = '';
  },

  methods: {
    handleKeydown(event) {
      switch (event.key) {
        case 'ArrowRight':
        case 'Enter':
          this.$emit('next');
          break;
        case 'ArrowLeft':
          if (!this.isFirstStep) {
            this.$emit('prev');
          }
          break;
        // Escape key skip removed - users must complete tutorial
      }
    },

    handleBackdropClick() {
      // Optional: advance on backdrop click
      // this.$emit('next');
    },

    handleDotClick(index) {
      this.$emit('go-to-step', index);
    }
  }
};
</script>

<style lang="scss" scoped>
.tutorial-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100002;
  pointer-events: auto;

  // When allowing interaction, let clicks pass through to target
  &.allow-interaction {
    pointer-events: none;

    // But keep tooltip and footer clickable
    .tutorial-tooltip-wrapper,
    .tutorial-footer {
      pointer-events: auto;
    }
  }
}

.tutorial-tooltip-wrapper {
  // Just a wrapper to control pointer-events
}

.tutorial-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  pointer-events: none;
}

.tutorial-spotlight {
  position: absolute;
  border-radius: 8px;
  box-shadow:
    0 0 0 9999px rgba(0, 0, 0, 0.5),
    0 0 20px 5px rgba(255, 255, 255, 0.4),
    inset 0 0 10px rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  pointer-events: none;

  // Add bright glow border
  &::after {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border: 3px solid rgba(255, 255, 255, 0.8);
    border-radius: 12px;
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.6);
    animation: pulse 2s infinite;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.tutorial-footer {
  position: fixed;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100004;

  // Safe area for iOS
  padding-bottom: env(safe-area-inset-bottom);
}

// Responsive adjustments
@media (max-width: 768px) {
  .tutorial-footer {
    bottom: 24px;
  }
}
</style>
